import { queryOptions } from "@tanstack/react-query";
import { api } from "./client";
import { payload } from "./envelope";
import type { components } from "./schema";

export type MinutesPolicy = components["schemas"]["MinutesEnvelope"]["data"];
export type DaysPolicy = components["schemas"]["DaysEnvelope"]["data"];

/**
 * As políticas vivem sob o prefixo `["establishment", id, "policies"]` com um
 * sufixo por política: a chave sem sufixo ficaria idêntica pra todas e o
 * TanStack Query conflaria os caches.
 */

/**
 * Por quanto tempo (minutos) um agendamento com sinal fica reservado
 * esperando pagamento antes de expirar. Sempre retorna um valor (nasce em 5).
 * Vale só pro sinal — a janela de resposta da fila tem régua própria
 * (`waitlistOfferPolicyQuery`).
 */
export function holdPolicyQuery(establishmentId: string) {
  return queryOptions({
    queryKey: ["establishment", establishmentId, "policies", "hold"] as const,
    queryFn: () =>
      payload(
        api.GET("/establishments/{id}/hold-policy", {
          params: { path: { id: establishmentId } },
        }),
      ),
  });
}

/** Em quantos dias uma entrada de fila sem resposta expira (nasce em 7). */
export function waitlistPolicyQuery(establishmentId: string) {
  return queryOptions({
    queryKey: ["establishment", establishmentId, "policies", "waitlist"] as const,
    queryFn: () =>
      payload(
        api.GET("/establishments/{id}/waitlist-policy", {
          params: { path: { id: establishmentId } },
        }),
      ),
  });
}

/**
 * Por quanto tempo (minutos) uma vaga oferecida pela fila fica reservada
 * esperando o cliente responder. Nasce em 120 (2h).
 *
 * Não confundir com `waitlistPolicyQuery`, que é por quantos DIAS uma entrada
 * da fila continua valendo: são os dois lados da mesma fila — quanto tempo a
 * pessoa espera nela, e quanto tempo ela tem pra responder quando a vez chega.
 *
 * É um teto, não uma promessa: o servidor encolhe o prazo efetivo pra no
 * máximo metade do tempo que falta pro atendimento.
 */
export function waitlistOfferPolicyQuery(establishmentId: string) {
  return queryOptions({
    queryKey: ["establishment", establishmentId, "policies", "waitlist-offer"] as const,
    queryFn: () =>
      payload(
        api.GET("/establishments/{id}/waitlist-offer-policy", {
          params: { path: { id: establishmentId } },
        }),
      ),
  });
}

/**
 * Tempo de troca (minutos) entre atendimentos, geral do estabelecimento —
 * `services.buffer_minutes` sobrescreve por serviço. Nasce em 0.
 */
export function bufferPolicyQuery(establishmentId: string) {
  return queryOptions({
    queryKey: ["establishment", establishmentId, "policies", "buffer"] as const,
    queryFn: () =>
      payload(
        api.GET("/establishments/{id}/buffer-policy", {
          params: { path: { id: establishmentId } },
        }),
      ),
  });
}

export function updateHoldPolicy(
  establishmentId: string,
  minutes: number,
): Promise<MinutesPolicy> {
  return payload(
    api.PUT("/establishments/{id}/hold-policy", {
      params: { path: { id: establishmentId } },
      body: { minutes },
    }),
  );
}

export function updateWaitlistOfferPolicy(
  establishmentId: string,
  minutes: number,
): Promise<MinutesPolicy> {
  return payload(
    api.PUT("/establishments/{id}/waitlist-offer-policy", {
      params: { path: { id: establishmentId } },
      body: { minutes },
    }),
  );
}

export function updateWaitlistPolicy(
  establishmentId: string,
  days: number,
): Promise<DaysPolicy> {
  return payload(
    api.PUT("/establishments/{id}/waitlist-policy", {
      params: { path: { id: establishmentId } },
      body: { days },
    }),
  );
}

export function updateBufferPolicy(
  establishmentId: string,
  minutes: number,
): Promise<MinutesPolicy> {
  return payload(
    api.PUT("/establishments/{id}/buffer-policy", {
      params: { path: { id: establishmentId } },
      body: { minutes },
    }),
  );
}
